// Shared canvas plumbing for the visualizers: devicePixelRatio-correct
// backing store (ResizeObserver + resolution media query), coalesced redraws
// (one per animation frame, only when something changed) and pointer
// hover/click for touch input.

export abstract class CanvasView<Hit> {
  protected readonly canvas: HTMLCanvasElement;
  protected readonly ctx: CanvasRenderingContext2D;
  /** CSS pixel size. */
  protected width = 0;
  protected height = 0;
  protected dpr = 1;
  protected hover: Hit | null = null;
  protected interactive = false;
  private ro: ResizeObserver | null = null;
  private mql: MediaQueryList | null = null;
  private frame = 0;
  private destroyed = false;
  private readonly listeners: [string, EventListener][] = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D is not available');
    this.ctx = ctx;
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(() => this.resize());
      try {
        this.ro.observe(canvas, { box: 'content-box' });
      } catch {
        this.ro.observe(canvas);
      }
    }
    this.watchDpr();
    this.listen('pointermove', (e) => this.onPointerMove(e as PointerEvent));
    this.listen('pointerleave', () => this.setHover(null));
    this.listen('click', (e) => this.onClick(e as MouseEvent));
  }

  private listen(type: string, fn: EventListener): void {
    this.canvas.addEventListener(type, fn);
    this.listeners.push([type, fn]);
  }

  private watchDpr(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const onChange = () => {
      this.mql?.removeEventListener('change', onChange);
      this.resize();
      this.watchDpr();
    };
    this.mql = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
    this.mql.addEventListener('change', onChange);
  }

  /** Re-measure the canvas (called automatically on size/zoom changes). */
  resize(): void {
    if (this.destroyed) return;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    if (w === this.width && h === this.height && dpr === this.dpr && this.canvas.width === Math.round(w * dpr)) return;
    this.width = w;
    this.height = h;
    this.dpr = dpr;
    this.canvas.width = Math.max(1, Math.round(w * dpr));
    this.canvas.height = Math.max(1, Math.round(h * dpr));
    this.onResize();
    this.draw();
  }

  /** Request a redraw on the next animation frame (coalesced). */
  protected invalidate(): void {
    if (this.destroyed || this.frame) return;
    if (typeof requestAnimationFrame === 'function') {
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.draw();
      });
    } else this.draw();
  }

  /** Draw immediately. */
  draw(): void {
    if (this.destroyed) return;
    if (this.frame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.frame);
    this.frame = 0;
    if (this.width <= 0 || this.height <= 0) {
      this.width = this.canvas.clientWidth;
      this.height = this.canvas.clientHeight;
      if (this.width <= 0 || this.height <= 0) return;
      this.canvas.width = Math.round(this.width * this.dpr);
      this.canvas.height = Math.round(this.height * this.dpr);
      this.onResize();
    }
    const c = this.ctx;
    c.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.paint(c, this.width, this.height);
  }

  private pointerXY(e: MouseEvent): [number, number] {
    const r = this.canvas.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.interactive) return;
    const [x, y] = this.pointerXY(e);
    this.setHover(this.hitTest(x, y));
  }

  private onClick(e: MouseEvent): void {
    if (!this.interactive) return;
    const [x, y] = this.pointerXY(e);
    const hit = this.hitTest(x, y);
    if (hit !== null) this.activate(hit);
  }

  protected setHover(hit: Hit | null): void {
    const same = hit === null || this.hover === null ? hit === this.hover : this.sameHit(hit, this.hover);
    if (same) return;
    this.hover = hit;
    this.canvas.style.cursor = hit !== null && this.interactive ? 'pointer' : '';
    this.invalidate();
  }

  protected setInteractive(on: boolean): void {
    if (this.interactive === on) return;
    this.interactive = on;
    if (!on) this.setHover(null);
  }

  /** Remove listeners and observers. */
  destroy(): void {
    this.destroyed = true;
    if (this.frame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.frame);
    this.ro?.disconnect();
    this.ro = null;
    this.mql = null;
    for (const [t, fn] of this.listeners) this.canvas.removeEventListener(t, fn);
    this.listeners.length = 0;
    this.canvas.style.cursor = '';
  }

  protected onResize(): void {}
  protected abstract paint(ctx: CanvasRenderingContext2D, width: number, height: number): void;
  protected abstract hitTest(x: number, y: number): Hit | null;
  protected abstract sameHit(a: Hit, b: Hit): boolean;
  protected abstract activate(hit: Hit): void;
}

/** Filled circle. */
export function dot(c: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string): void {
  c.beginPath();
  c.arc(x, y, r, 0, Math.PI * 2);
  c.fillStyle = fill;
  c.fill();
}

/** Ring. */
export function ring(c: CanvasRenderingContext2D, x: number, y: number, r: number, stroke: string, width: number): void {
  c.beginPath();
  c.arc(x, y, r, 0, Math.PI * 2);
  c.strokeStyle = stroke;
  c.lineWidth = width;
  c.stroke();
}

export function roundRect(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  c.beginPath();
  c.moveTo(x + rr, y);
  c.lineTo(x + w - rr, y);
  c.arcTo(x + w, y, x + w, y + rr, rr);
  c.lineTo(x + w, y + h - rr);
  c.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  c.lineTo(x + rr, y + h);
  c.arcTo(x, y + h, x, y + h - rr, rr);
  c.lineTo(x, y + rr);
  c.arcTo(x, y, x + rr, y, rr);
  c.closePath();
}

/** Text fitted into a width by shrinking the font (min 7px). */
export function fitText(c: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, weight = 600, size = 11): void {
  let s = size;
  c.font = `${weight} ${s}px Inter, system-ui, sans-serif`;
  while (s > 7 && c.measureText(text).width > maxW) {
    s -= 1;
    c.font = `${weight} ${s}px Inter, system-ui, sans-serif`;
  }
  c.fillText(text, x, y);
}
